import { forwardRef } from "react";
import { Paper, Snackbar as MuiSnackbar } from "@mui/material";

import { tokens } from "../../tokens/generated/tokens";

import type { AlertProps } from "../Alert";
import type { SxProps, Theme } from "@mui/material/styles";
import type { SnackbarProps as MuiSnackbarProps } from "@mui/material/Snackbar";
import type { ReactElement } from "react";

const { fgColor, bgColor } = tokens.theme.light;
const dark = tokens.theme.dark;
const { space, borderRadius } = tokens.functional.size;
const { typography, size } = tokens.base;
const { fontStack } = typography;
const { body } = tokens.typography.typography.text;
const BARE_CONTENT_MIN_WIDTH = 320;
const BARE_CONTENT_MAX_WIDTH = 496;

const DEFAULT_ANCHOR_ORIGIN: MuiSnackbarProps["anchorOrigin"] = {
  vertical: "bottom",
  horizontal: "left",
};
const DEFAULT_AUTO_HIDE_DURATION = 6000;

/** Montserrat from the design token, falling back per the Kaizen type spec. */
const sansSerifFontStack = `${fontStack.sansSerif}, Avenir, Corbel, 'URW Gothic', 'Helvetica Neue', Arial, sans-serif`;

const frameSx: SxProps<Theme> = (theme) => ({
  minWidth: BARE_CONTENT_MIN_WIDTH,
  maxWidth: BARE_CONTENT_MAX_WIDTH,
  borderRadius: theme.typography.pxToRem(borderRadius.default),
});

const bareContentSx: SxProps<Theme> = (theme) => ({
  minWidth: BARE_CONTENT_MIN_WIDTH,
  maxWidth: BARE_CONTENT_MAX_WIDTH,
  borderRadius: theme.typography.pxToRem(borderRadius.default),
  backgroundColor: bgColor.inverse,
  color: fgColor.onEmphasis,
  fontFamily: sansSerifFontStack,
  fontSize: theme.typography.pxToRem(body.fontSize.medium),
  lineHeight: body.lineHeight.medium,
  fontWeight: body.fontWeight,
  letterSpacing: body.letterSpacing.medium,
  paddingBlock: theme.typography.pxToRem(space.small),
  paddingInline: theme.typography.pxToRem(space.xlarge),
  "& .MuiSnackbarContent-action": {
    gap: theme.typography.pxToRem(space.medium),
    marginRight: theme.typography.pxToRem(size.negative8),
  },
  "& .MuiSnackbarContent-action .MuiButtonBase-root.MuiButton-root": {
    color: `${fgColor.onEmphasis} !important`,
  },
  // The inverse bar flips with the scheme: a near-black bar with white text in
  // light, a near-white one with near-black text in dark. Excluded inside a
  // nearer scope that pins light — `appRoutes` wraps the pre-auth pages that
  // way, and an unguarded descendant selector would still match through it.
  '[data-mui-color-scheme="dark"] &:not([data-mui-color-scheme="light"] *)': {
    backgroundColor: dark.bgColor.inverse,
    color: dark.fgColor.onEmphasis,
    "& .MuiSnackbarContent-action .MuiButtonBase-root.MuiButton-root": {
      color: `${dark.fgColor.onEmphasis} !important`,
    },
  },
});

export interface ToastProps extends Omit<MuiSnackbarProps, "children"> {
  /**
   * A Centro `Alert`, hosted edge-to-edge in the Toast frame. Omit for the
   * bare inverse bar.
   */
  children?: ReactElement<AlertProps>;
  /** Root test id. Default: "ds-toast". */
  "data-testid"?: string;
}

/** Centro/Kaizen wrapper around MUI Snackbar: a bare inverse bar, or an edge-to-edge hosted Alert. */
export const Toast = forwardRef<HTMLDivElement, ToastProps>((props, ref) => {
  const {
    anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
    autoHideDuration = DEFAULT_AUTO_HIDE_DURATION,
    children,
    slotProps,
    "data-testid": dataTestId = "ds-toast",
    ...rest
  } = props;

  const hostedAlertSeverity = children?.props.severity;
  const resolvedAutoHideDuration =
    hostedAlertSeverity === "error" ? null : autoHideDuration;

  if (children) {
    return (
      <MuiSnackbar
        ref={ref}
        anchorOrigin={anchorOrigin}
        autoHideDuration={resolvedAutoHideDuration}
        data-testid={dataTestId}
        slotProps={slotProps}
        {...rest}
      >
        <Paper
          elevation={6}
          data-testid={`${dataTestId}-frame`}
          sx={[frameSx, { overflow: "hidden" }] as SxProps<Theme>}
        >
          {children}
        </Paper>
      </MuiSnackbar>
    );
  }

  const contentSlotProps = slotProps?.content;
  const resolvedContentSlotProps =
    typeof contentSlotProps === "function"
      ? (...args: Parameters<typeof contentSlotProps>) => {
          const resolved = contentSlotProps(...args);
          return {
            "data-testid": `${dataTestId}-content`,
            ...resolved,
            sx: [bareContentSx, resolved?.sx] as SxProps<Theme>,
          };
        }
      : {
          "data-testid": `${dataTestId}-content`,
          ...contentSlotProps,
          sx: [bareContentSx, contentSlotProps?.sx] as SxProps<Theme>,
        };

  return (
    <MuiSnackbar
      ref={ref}
      anchorOrigin={anchorOrigin}
      autoHideDuration={resolvedAutoHideDuration}
      data-testid={dataTestId}
      slotProps={{ ...slotProps, content: resolvedContentSlotProps }}
      {...rest}
    />
  );
});

Toast.displayName = "Toast";
